import React from 'react'
import { sessionStorage } from 'react-storage'
import getConfig from 'next/config'
import {
  OptimizelyProvider,
  createInstance,
  enums,
} from '@optimizely/react-sdk'
import { isPlainObject } from 'lodash'
import { useCookies } from '@src/utils/cookies'
import { useUserSession } from '@utils/useUserSession'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { isServer } from '@src/utils/isServer'
import { sendEvent } from '@utils/analytics/coreAnalytics'
import { useRouter } from '@src/routes'
import { useMediaQuery } from '@mui/material'
import { theme } from '@src/styles/theme'
import { parseRegionalUrl } from '@src/utils/regional'
import { customOptlyLogger, noOpTrashLogs } from './optimizelyLoggers'
import { OptimizelyDataFileType } from '@src/utils/getOptimizelyDataFile'

const OPTLY_SEEN_FLAG_KEYS = 'optimizely_seen_flag_keys'

const hasOptlyFlagBeenSeen = (flagKey: string): boolean => {
  if (isServer()) return true // Window.sessionstorage can't be used on the server. Return true to stop execution and prevent logging.

  // Grabs the raw Optimizely flag key list from session storage, if it exists. Is undefined if not yet set.
  const seenFlagKeysRaw = sessionStorage.getItem(OPTLY_SEEN_FLAG_KEYS)

  /*
    Attempt to parse the raw session storage data to a javascript array since it must be stored as a JSON string.
    If the storage item isn't defined we'll make a new array.
    In the array exists with the key we return true.
    If the key doesn't exist in the array we try to add it to the array and write updated session storage.
    At any point if there are problems parsing or grabbing session storage we just throw an error and let the function return false.
    This will halt any event sends for Optimizely flags.
  */
  try {
    const seenFlagsList =
      seenFlagKeysRaw && Array.isArray(seenFlagKeysRaw) ? seenFlagKeysRaw : []

    if (seenFlagsList.includes(flagKey)) {
      return true
    }

    seenFlagsList.push(flagKey)
    sessionStorage.setItem(OPTLY_SEEN_FLAG_KEYS, seenFlagsList)
  } catch (optlyFlagListError) {
    console.error(
      'A problem occured when parsing the Optimizely seen feature flag values. No flag events will be sent.',
      optlyFlagListError
    )
  }

  return false
}

const {
  publicRuntimeConfig: { optimizelyFeatureFlagId },
} = getConfig()

const SIALOptimizelyProvider: React.FC<{
  datafile: OptimizelyDataFileType
  children: React.ReactNode
}> = ({ datafile, children }) => {
  const [cookies] = useCookies(['GUID', 'InternalUser'])
  const { userIsLoggedIn } = useCurrentUser({})
  const { userSession } = useUserSession()
  const router = useRouter()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true })
  const device = isDesktop ? 'desktop' : 'mobile'

  /*
  We want to control if Optimizely should log SDK behavior to the console or not under certain situations involving the datafile...
  1. Bot traffic we do not want to run experiments on. You'll get an empty datafile, this could be an error for a normal user. For bots it is intended so in those cases we shut off logging.
  2. If a user does not have a valid level of cookie consent to be tracked or experimented on we give an invalid datafile. If this is the case Optimizely shouldn't log anything.
  3. If the Optimizely file is not a JS object something is likely wrong with it's response and errors would be reported in the getOptimizelyDataFile() function. Additional logs are just clutter.
  In these cases we don't want Optimizely to get a valid datafile or SDK and to silence all of the providers logs.
  */
  const isInvalidDatafile =
    isPlainObject(datafile) && !datafile?.isBot && datafile?.hasCookieConsent

  // Similar we only want to provide the SDK key if it is set in the config, the user isn't a bot, and they've consented to cookie tracking.
  const isValidSdkUser =
    optimizelyFeatureFlagId && !datafile?.isBot && datafile?.hasCookieConsent

  const optimizelyInstance = createInstance({
    datafile: datafile?.datafile,
    sdkKey: isValidSdkUser ? optimizelyFeatureFlagId : undefined,
    datafileOptions: { autoUpdate: !isServer() },
    odpOptions: { disabled: true },
    eventBatchSize: isServer() ? 1 : 10,
    eventMaxQueueSize: isServer() ? 1 : 100,
    eventFlushInterval: isServer() ? 3000 : 1000,
    logger: {
      log: isInvalidDatafile ? customOptlyLogger : noOpTrashLogs,
    },
  })

  const guidCookie = cookies['GUID'] || 'default_user'

  const { pathAfterBasename } = parseRegionalUrl(router.asPath)

  /**
   * UIPT-406 - Optly Feature Flags Connection to GA4
   * https://docs.developers.optimizely.com/feature-experimentation/docs/set-up-google-analytics-4-ga4
   */
  const onDecision = ({ type, decisionInfo }): void => {
    // Add a DECISION Notification Listener for type FLAG
    if (type === 'flag') {
      const isFlagAlreadyLogged = hasOptlyFlagBeenSeen(decisionInfo.flagKey)

      if (!isFlagAlreadyLogged) {
        sendEvent({
          payload: {
            event: 'optimizely-decision-fs',
            'optimizely-flagKey': decisionInfo.flagKey,
            'optimizely-ruleKey': decisionInfo.ruleKey,
            'optimizely-variationKey': decisionInfo.variationKey,
          },
        })
      }
    }
  }

  optimizelyInstance?.notificationCenter?.addNotificationListener(
    enums.NOTIFICATION_TYPES.DECISION,
    // @ts-ignore
    onDecision
  )

  return (
    <OptimizelyProvider
      isServerSide={isServer()}
      optimizely={optimizelyInstance}
      user={{
        id: guidCookie,
        attributes: {
          userIsLoggedIn: userIsLoggedIn,
          locale: `${userSession.language}_${userSession.country}`,
          country: userSession.country,
          internalUser: cookies?.internalUser || false,
          language: userSession.language,
          route: router.asPath,
          device: isServer() ? 'server' : device,
          pathAfterBasename: pathAfterBasename || '',
        },
      }}
    >
      {children}
    </OptimizelyProvider>
  )
}

export default SIALOptimizelyProvider
